import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import AdminLayout from '../../components/Admin/AdminLayout'
import { AdminKpiCard, trendDirFromPct } from './adminUi'
import { PaymentReviewTable } from '../../components/Admin/PaymentReviewModal'
import { IconLabel, RsIcon, ModalClose } from '../../components/icons'

const PAGE_SIZE = 8

const TYPE_META = {
  report: { icon: 'alert', label: 'Report' },
  dispute: { icon: 'dispute', label: 'Dispute' },
  inquiry: { icon: 'feedback', label: 'Inquiry' },
  feedback: { icon: 'chat', label: 'Feedback' },
  payment: { icon: 'payment', label: 'Payment' },
}

const PRIORITY_META = {
  high: { icon: 'alert', label: 'High' },
  medium: { icon: 'clock', label: 'Medium' },
  low: { icon: 'check', label: 'Low' },
}

const STATUS_META = {
  pending: { icon: 'clock', label: 'Pending' },
  reviewing: { icon: 'search', label: 'Reviewing' },
  escalated: { icon: 'alert', label: 'Escalated' },
  resolved: { icon: 'check', label: 'Resolved' },
}

function avatarColor(name) {
  const colors = ['#0f3d8a', '#7c3aed', '#16a34a', '#ea580c', '#dc2626', '#67c23a']
  return colors[(name?.charCodeAt(0) || 0) % colors.length]
}

function initials(name) {
  return (name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
}

function formatRelativeTime(d) {
  if (!d) return '—'
  const diff = Date.now() - new Date(d).getTime()
  if (diff < 0) return 'just now'
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
}

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || { icon: 'fileText', label: type || '—' }
  return (
    <span className={`admin-type-badge ${type || ''}`}>
      <IconLabel name={meta.icon} size="xs">{meta.label}</IconLabel>
    </span>
  )
}

function PriorityText({ priority }) {
  const meta = PRIORITY_META[priority] || { icon: 'clock', label: priority || '—' }
  return (
    <span className={`admin-priority-text ${priority || ''}`}>
      <IconLabel name={meta.icon} size="xs">{meta.label}</IconLabel>
    </span>
  )
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { icon: 'clock', label: status || '—' }
  return (
    <span className={`admin-status-badge ${status || ''}`}>
      <IconLabel name={meta.icon} size="xs">{meta.label}</IconLabel>
    </span>
  )
}

export default function AdminMessages() {
  const navigate = useNavigate()
  const [tab, setTab] = useState('messages')
  const [stats, setStats] = useState(null)
  const [queue, setQueue] = useState([])
  const [tabCounts, setTabCounts] = useState({ messages: 0, compliance: 0, reports: 0 })
  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [detail, setDetail] = useState(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/messages-compliance')
      setStats(res.data.stats)
      setQueue(res.data.queue || [])
      setTabCounts(res.data.tabCounts || {})
    } catch {
      toast.error('Failed to load messages data')
    }
  }, [])

  useEffect(() => { load() }, [load])

  const filtered = useMemo(() => queue.filter((q) => {
    if (q.tab !== tab) return false
    if (typeFilter !== 'all' && q.type !== typeFilter) return false
    if (statusFilter !== 'all' && q.status !== statusFilter) return false
    if (priorityFilter !== 'all' && q.priority !== priorityFilter) return false
    if (search) {
      const s = search.toLowerCase()
      if (!q.subject?.toLowerCase().includes(s)
        && !q.from_name?.toLowerCase().includes(s)
        && !q.from_email?.toLowerCase().includes(s)
        && !q.case_id?.toLowerCase().includes(s)) return false
    }
    return true
  }), [queue, tab, typeFilter, statusFilter, priorityFilter, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => { setPage(1) }, [tab, typeFilter, statusFilter, priorityFilter, search])

  const resetFilters = () => {
    setTypeFilter('all')
    setStatusFilter('all')
    setPriorityFilter('all')
    setSearch('')
  }

  const handleResolve = async (item) => {
    setBusy(true)
    try {
      await axios.post(`/api/admin/compliance-cases/${item.source}/${item.id}/resolve`, {
        action: 'approve',
        notes: 'Resolved from Messages queue',
      })
      toast.success('Case resolved')
      setDetail(null)
      load()
      window.dispatchEvent(new Event('rs-admin-badges-changed'))
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed')
    } finally {
      setBusy(false)
    }
  }

  const handleEscalate = async (item) => {
    setBusy(true)
    try {
      await axios.post(`/api/admin/compliance-cases/${item.source}/${item.id}/resolve`, {
        action: 'escalate',
        notes: 'Escalated from Messages queue',
      })
      toast.success('Case escalated')
      setDetail(null)
      load()
      window.dispatchEvent(new Event('rs-admin-badges-changed'))
    } catch (err) {
      toast.error(err.response?.data?.message || 'Escalation failed')
    } finally {
      setBusy(false)
    }
  }

  const tabs = [
    { id: 'messages', label: 'Messages', icon: 'inbox', count: tabCounts.messages },
    { id: 'compliance', label: 'Compliance', icon: 'dispute', count: tabCounts.compliance },
    { id: 'reports', label: 'Reports', icon: 'alert', count: tabCounts.reports },
  ]

  const tableTitle = tab === 'messages'
    ? 'Support Messages'
    : tab === 'compliance'
      ? 'Compliance Queue'
      : 'User Reports'

  return (
    <AdminLayout>
      <div className="admin-tabs-bar">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`admin-tab-btn${tab === t.id ? ' active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <IconLabel name={t.icon} size="xs">{t.label}</IconLabel>
            {t.count > 0 && <span className="admin-tab-badge">{t.count}</span>}
          </button>
        ))}
      </div>

      <div className="admin-kpi-row cols-5">
        <AdminKpiCard icon="inbox" color="blue" label="Unread Messages" value={stats?.unreadMessages ?? '—'} trend={stats?.unreadTrend} trendDir={trendDirFromPct(stats?.unreadTrend)} />
        <AdminKpiCard icon="clock" color="orange" label="Pending Cases" value={stats?.pendingCases ?? '—'} />
        <AdminKpiCard icon="alert" color="red" label="Escalated" value={stats?.escalated ?? '—'} />
        <AdminKpiCard icon="check" color="green" label="Resolved (MTD)" value={stats?.resolvedMtd ?? '—'} />
        <AdminKpiCard icon="clock" color="purple" label="Avg. Open Case Age" value={stats?.avgResponse ?? '—'} />
      </div>

      <div className="admin-table-card">
        <div className="admin-table-header">
          <span className="admin-table-title">{tableTitle}</span>
        </div>
        <div className="admin-filter-row">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All Types</option>
            <option value="report">Report</option>
            <option value="dispute">Dispute</option>
            <option value="inquiry">Inquiry</option>
            <option value="feedback">Feedback</option>
            <option value="payment">Payment</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="reviewing">Reviewing</option>
            <option value="escalated">Escalated</option>
            <option value="resolved">Resolved</option>
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
            <option value="all">All Priority</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <input
            className="admin-filter-search"
            placeholder="Search by subject or user..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="admin-btn sm secondary" onClick={resetFilters}>Reset</button>
        </div>

        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Type</th>
              <th>Subject</th>
              <th>From</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Created</th>
              <th className="admin-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', color: '#909399', padding: 32 }}>
                  No cases match your filters
                </td>
              </tr>
            ) : pageItems.map((q) => (
              <tr key={`${q.case_id}-${q.id}`} className={q.status === 'pending' ? 'row-pending' : ''}>
                <td><span className="admin-case-id">{q.case_id}</span></td>
                <td><TypeBadge type={q.type} /></td>
                <td><span className="admin-msg-subject">{q.subject}</span></td>
                <td>
                  <div className="admin-msg-user-cell">
                    <span className="admin-user-avatar" style={{ background: avatarColor(q.from_name) }}>
                      {initials(q.from_name)}
                    </span>
                    <div className="admin-msg-user-info">
                      <span className="admin-user-name">{q.from_name}</span>
                      <span className="admin-user-email">{q.from_email}</span>
                    </div>
                  </div>
                </td>
                <td><PriorityText priority={q.priority} /></td>
                <td><StatusBadge status={q.status} /></td>
                <td><span className="admin-created-time">{formatRelativeTime(q.created_at)}</span></td>
                <td className="admin-td-actions admin-actions-cell">
                  <div className="admin-action-btns">
                    <button type="button" className="admin-action-btn view" onClick={() => setDetail(q)}>View</button>
                    {q.status !== 'resolved' && (
                      q.status === 'reviewing' ? (
                        <button type="button" className="admin-action-btn escalate" disabled={busy} onClick={() => handleEscalate(q)}>Escalate</button>
                      ) : (
                        <button
                          type="button"
                          className="admin-action-btn resolve"
                          onClick={() => handleResolve(q)}
                        >
                          Resolve
                        </button>
                      )
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="admin-pagination">
          <span className="admin-pg-info">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="admin-pg-controls">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((n) => (
              <button key={n} type="button" className={page === n ? 'active' : ''} onClick={() => setPage(n)}>{n}</button>
            ))}
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
          </div>
        </div>
      </div>

      {detail && (
        <div className="admin-modal-overlay show" onClick={() => setDetail(null)}>
          <div className="admin-modal admin-modal-scroll" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Case {detail.case_id}</h3>
              <ModalClose onClick={() => setDetail(null)} />
            </div>
            <div className="admin-modal-body">
              <div className="admin-detail-row"><span className="admin-detail-label">Subject</span><span>{detail.subject}</span></div>
              <div className="admin-detail-row">
                <span className="admin-detail-label">From</span>
                <span>{detail.from_name} ({detail.from_email})</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-label">Type</span>
                <TypeBadge type={detail.type} />
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-label">Priority</span>
                <PriorityText priority={detail.priority} />
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-label">Status</span>
                <StatusBadge status={detail.status} />
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-label">Created</span>
                <span>{formatRelativeTime(detail.created_at)}</span>
              </div>
              {detail.type === 'payment' && detail.paymentTable && (
                <div className="admin-detail-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                  <span className="admin-detail-label">Payment details (system-generated)</span>
                  <PaymentReviewTable table={detail.paymentTable} />
                </div>
              )}
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn secondary" onClick={() => setDetail(null)}>Close</button>
              {detail.type === 'payment' && detail.orderId && detail.status !== 'resolved' && (
                <button
                  type="button"
                  className="admin-btn admin-btn-success"
                  onClick={() => {
                    navigate(`/admin/payments?review=${detail.orderId}`)
                    setDetail(null)
                  }}
                >
                  Review & approve
                </button>
              )}
              {detail.type !== 'payment' && detail.status !== 'resolved' && (
                <span className="admin-action-capsule">
                  <button
                    type="button"
                    className="admin-action-btn resolve"
                    disabled={busy}
                    onClick={() => handleResolve(detail)}
                  >
                    Mark Resolved
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      )}

    </AdminLayout>
  )
}
